import React, { PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ContainerBase } from './containerBase';

// Generic vertical layout
const LeftSidebarLayoutContainer = styled.div<{ $sidebarStickyTop: number }>`
    --sidebar-sticky-top: ${(props) => props.$sidebarStickyTop}px;
    position: relative;
`;

const LeftSidebarLayoutHeader = styled.div`
    display: flex;
    flex-direction: column;
    /* The measuring wrapper must also be sticky; otherwise it bounds the PageHeader sticky child to the wrapper's own height. */
    position: sticky;
    top: 0;
    z-index: 2;
`;

const LeftSidebarLayoutOuter = styled.div`
    display: flex;
    /* Keep sticky sidebars at their natural height. Flex stretch would make them as tall as the content column, leaving no room to stick. */
    align-items: flex-start;
    gap: 24px;
    position: relative;
`;

const LeftSidebarLayoutInner = styled(ContainerBase)`
    flex: 1;
`;

export type SidebarItemIdType = string | number;

export interface SidebarItem<T extends SidebarItemIdType = string> {
    id: T;
    label: string;
    selected?: boolean;
    disabled?: boolean;
    subItems?: SidebarItem<T>[];
    buttons?: SidebarButton[];
    position?: number;
    // Optional node rendered in place of the action buttons (e.g. a "Coming soon" tag). When set, the row is not
    // clickable (used for locked default/paid-package categories).
    badge?: React.ReactNode;
}

export interface SidebarButton {
    key: string;
    tooltip?: string;
    icon: React.ReactElement<React.SVGAttributes<SVGElement>>;
    isActive?: boolean;
    id?: string;
}

interface LeftSidebarLayoutProps {
    sidebar?: React.ReactNode;
    header?: React.ReactNode;
}

export const LeftSidebarLayout = ({ children, header, sidebar }: PropsWithChildren<LeftSidebarLayoutProps>) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const [sidebarStickyTop, setSidebarStickyTop] = useState(0);
    const hasHeader = Boolean(header);

    useLayoutEffect(() => {
        if (!hasHeader || !headerRef.current) {
            setSidebarStickyTop(0);
            return;
        }

        const headerElement = headerRef.current;
        const updateStickyTop = () => {
            // Page headers are sticky too; offset the sidebar by the current header height so they do not overlap while scrolling.
            setSidebarStickyTop(Math.ceil(headerElement.getBoundingClientRect().height));
        };

        updateStickyTop();

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateStickyTop) : null;
        resizeObserver?.observe(headerElement);
        window.addEventListener('resize', updateStickyTop);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateStickyTop);
        };
    }, [hasHeader]);

    return (
        <LeftSidebarLayoutContainer $sidebarStickyTop={sidebarStickyTop}>
            {header && <LeftSidebarLayoutHeader ref={headerRef}>{header}</LeftSidebarLayoutHeader>}
            <LeftSidebarLayoutOuter>
                {sidebar}
                <LeftSidebarLayoutInner>{children}</LeftSidebarLayoutInner>
            </LeftSidebarLayoutOuter>
        </LeftSidebarLayoutContainer>
    );
};
